import ClarityLogo from "@/assets/clarity-logo-light.svg";
import DtoolsPdfQuotationPageShell from "@/components/sales/dtools/DtoolsPdfQuotationPageShell";
import {
  BOM_PRINT_BASE_FONT_SIZE_PX,
  BOM_PRINT_BASE_LINE_HEIGHT,
  BOM_PRINT_FONT,
  BOM_PRINT_LABEL,
  BOM_PRINT_TEXT,
} from "@/lib/bomPrintDesign";
import { PDF_QR_IMAGE_ATTR } from "@/lib/proposalPdfExportCanvas";
import {
  PDF_CLOSING_LOGO_CLASS,
  PDF_CLOSING_LOGO_HEIGHT_PX,
  PDF_CLOSING_LOGO_STYLE,
} from "@/lib/proposalPdfQuotationLayout";
import { PROPOSAL_BG } from "@/lib/proposalTheme";
import QRCode from "qrcode";
import { useEffect, useState } from "react";

type Props = {
  linkBUrl: string;
  proposalRef: string;
};

/**
 * Generate QR as a PNG data URL and render a normal <img>.
 * Avoids html2canvas canvas-bitmap clone failures that left blank QR frames.
 * Export also re-ensures via ensureProposalPdfQrImages before capture.
 */
export function PdfQrImage({ value }: { value: string }) {
  const [dataUrl, setDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDataUrl(null);
    void QRCode.toDataURL(value, {
      width: 360,
      margin: 2,
      errorCorrectionLevel: "M",
      color: { dark: "#1A2744", light: "#FFFFFF" },
    })
      .then((url) => {
        if (cancelled) return;
        if (!url || !url.startsWith("data:image/") || url.length < 64) return;
        setDataUrl(url);
      })
      .catch(() => {
        // Export prepare regenerates via ensureProposalPdfQrImages.
      });
    return () => {
      cancelled = true;
    };
  }, [value]);

  if (!dataUrl) {
    return (
      <div
        className="mx-auto inline-flex h-[168px] w-[168px] items-center justify-center border border-[#1A2744]/12 bg-white"
        data-testid="dtools-pdf-closing-qr"
        data-pdf-qr-value={value}
        aria-busy="true"
      />
    );
  }

  return (
    <div
      className="mx-auto inline-flex border border-[#1A2744]/15 bg-white p-3"
      data-testid="dtools-pdf-closing-qr"
      data-pdf-qr-value={value}
    >
      <img
        src={dataUrl}
        alt="Proposal QR code"
        width={168}
        height={168}
        decoding="sync"
        loading="eager"
        data-testid="dtools-pdf-closing-qr-image"
        data-pdf-qr-image="true"
        style={{ display: "block", width: "168px", height: "168px", background: "#FFFFFF" }}
      />
    </div>
  );
}

export default function DtoolsPdfPackageClosingPage({ linkBUrl, proposalRef }: Props) {
  const url = linkBUrl.trim();
  const ref = proposalRef.trim() || "—";

  return (
    <DtoolsPdfQuotationPageShell testId="dtools-pdf-closing-page" className="pdf-keep-together">
      <div
        style={{
          fontFamily: BOM_PRINT_FONT,
          color: BOM_PRINT_TEXT,
          fontSize: BOM_PRINT_BASE_FONT_SIZE_PX,
          lineHeight: BOM_PRINT_BASE_LINE_HEIGHT,
          background: PROPOSAL_BG,
        }}
      >
        <div className="mb-10 flex justify-end">
          <img
            src={ClarityLogo}
            alt="Clarity"
            className={PDF_CLOSING_LOGO_CLASS}
            style={PDF_CLOSING_LOGO_STYLE}
            crossOrigin="anonymous"
            data-testid="dtools-pdf-closing-logo"
            height={PDF_CLOSING_LOGO_HEIGHT_PX}
          />
        </div>

        <div className="mx-auto max-w-[440px] space-y-5 text-center">
          <h2
            style={{
              margin: 0,
              fontSize: 16,
              fontWeight: 700,
              letterSpacing: "0.04em",
              color: BOM_PRINT_TEXT,
            }}
            data-testid="dtools-pdf-closing-title"
          >
            Continue your experience online
          </h2>
          <p style={{ margin: 0, fontSize: 11, lineHeight: 1.55, color: BOM_PRINT_TEXT }}>
            Scan the QR code below to continue with your interactive proposal.
          </p>
          <p style={{ margin: 0, fontSize: 10, lineHeight: 1.55, color: BOM_PRINT_LABEL }}>
            The online proposal contains the full interactive experience including approvals, project
            updates, images and videos.
          </p>

          {url ? <PdfQrImage value={url} /> : null}

          {url ? (
            <div className="space-y-2">
              <p style={{ margin: 0, fontSize: 10, color: BOM_PRINT_LABEL }}>or click below</p>
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block font-semibold underline underline-offset-4"
                style={{ fontSize: 12, color: BOM_PRINT_TEXT }}
                data-testid="dtools-pdf-closing-url"
                data-pdf-external-link="true"
              >
                Open Interactive Proposal
              </a>
            </div>
          ) : null}

          <p
            className="font-semibold tracking-[0.14em]"
            style={{ fontSize: 10, color: "#374151", marginTop: 8 }}
            data-testid="dtools-pdf-closing-ref"
          >
            {ref}
          </p>
        </div>
      </div>
    </DtoolsPdfQuotationPageShell>
  );
}
